import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Settings, Lock, Unlock } from 'lucide-react'
import { RedTipologiaDocumentoCustom, RedOperatoreAvviso } from '../../types/red'
import { TIPOLOGIE_DOCUMENTO_LABEL } from '../../constants/red'
import { tipologieDocumentoApi, operatoriAvvisoApi, configApi } from '../../services/api'
import NuovaTipologiaDocumentoModal from '../../components/red/NuovaTipologiaDocumentoModal'
import NuovoOperatoreAvvisoModal from '../../components/red/NuovoOperatoreAvvisoModal'

const RedImpostazioni: React.FC = () => {
  const [custom, setCustom] = useState<RedTipologiaDocumentoCustom[]>([])
  const [loading, setLoading] = useState(true)
  const [showNuova, setShowNuova] = useState(false)

  const [operatori, setOperatori] = useState<RedOperatoreAvviso[]>([])
  const [loadingOperatori, setLoadingOperatori] = useState(true)
  const [showNuovoOperatore, setShowNuovoOperatore] = useState(false)

  const [giorniPreavviso, setGiorniPreavviso] = useState('30, 7, 1')
  const [canaliEmail, setCanaliEmail] = useState(true)
  const [canaliWhatsapp, setCanaliWhatsapp] = useState(true)
  const [loadingCadenza, setLoadingCadenza] = useState(true)
  const [savingCadenza, setSavingCadenza] = useState(false)

  const [solaLettura, setSolaLettura] = useState(false)
  const [puoGestireSolaLettura, setPuoGestireSolaLettura] = useState(false)
  const [loadingSolaLettura, setLoadingSolaLettura] = useState(true)
  const [savingSolaLettura, setSavingSolaLettura] = useState(false)

  const loadSolaLettura = () => {
    configApi.getRedSolaLettura()
      .then(res => {
        setSolaLettura(res.data.attivo)
        setPuoGestireSolaLettura(res.data.puo_gestire)
        setLoadingSolaLettura(false)
      })
      .catch(() => setLoadingSolaLettura(false))
  }

  const handleToggleSolaLettura = () => {
    const nuovoValore = !solaLettura
    setSavingSolaLettura(true)
    configApi.setRedSolaLettura(nuovoValore)
      .then(() => {
        setSolaLettura(nuovoValore)
        toast.success(nuovoValore ? 'Modulo RED impostato in sola lettura' : 'Modulo RED sbloccato: tutti possono tornare a modificare')
      })
      .catch(() => toast.error('Errore aggiornamento sola lettura'))
      .finally(() => setSavingSolaLettura(false))
  }

  const load = () => {
    tipologieDocumentoApi.list()
      .then(res => {
        setCustom(res.data)
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
        toast.error('Errore caricamento tipologie documento')
      })
  }

  const loadOperatori = () => {
    operatoriAvvisoApi.list()
      .then(res => {
        setOperatori(res.data)
        setLoadingOperatori(false)
      })
      .catch(() => {
        setLoadingOperatori(false)
        toast.error('Errore caricamento operatori avviso')
      })
  }

  const loadCadenza = () => {
    operatoriAvvisoApi.getCadenza()
      .then(res => {
        setGiorniPreavviso((res.data.giorni_preavviso_default as number[]).join(', '))
        setCanaliEmail((res.data.canali_default as string[]).includes('email'))
        setCanaliWhatsapp((res.data.canali_default as string[]).includes('whatsapp'))
        setLoadingCadenza(false)
      })
      .catch(() => {
        setLoadingCadenza(false)
        toast.error('Errore caricamento cadenza avvisi')
      })
  }

  useEffect(load, [])
  useEffect(loadOperatori, [])
  useEffect(loadCadenza, [])
  useEffect(loadSolaLettura, [])

  const handleToggleAttivo = async (tipo: RedTipologiaDocumentoCustom) => {
    try {
      await tipologieDocumentoApi.update(tipo.id, !tipo.attivo)
      toast.success(tipo.attivo ? 'Tipologia disattivata' : 'Tipologia riattivata')
      load()
    } catch {
      toast.error('Errore aggiornamento tipologia')
    }
  }

  const handleToggleOperatore = async (op: RedOperatoreAvviso) => {
    try {
      await operatoriAvvisoApi.update(op.id, { attivo: !op.attivo })
      toast.success(op.attivo ? 'Operatore disattivato' : 'Operatore riattivato')
      loadOperatori()
    } catch {
      toast.error('Errore aggiornamento operatore')
    }
  }

  const handleEliminaOperatore = async (op: RedOperatoreAvviso) => {
    if (!window.confirm(`Eliminare l'operatore "${op.nome}"? L'azione non è reversibile.`)) return
    try {
      await operatoriAvvisoApi.delete(op.id)
      toast.success('Operatore eliminato')
      loadOperatori()
    } catch {
      toast.error('Errore eliminazione operatore')
    }
  }

  const handleSalvaCadenza = async () => {
    const giorni = giorniPreavviso
      .split(',')
      .map(g => parseInt(g.trim(), 10))
      .filter(g => !isNaN(g) && g >= 0)
    if (giorni.length === 0) {
      toast.error('Inserisci almeno un giorno di preavviso valido')
      return
    }
    const canali: string[] = []
    if (canaliEmail) canali.push('email')
    if (canaliWhatsapp) canali.push('whatsapp')
    if (canali.length === 0) {
      toast.error('Seleziona almeno un canale')
      return
    }
    setSavingCadenza(true)
    try {
      await operatoriAvvisoApi.updateCadenza({ giorni_preavviso_default: giorni, canali_default: canali })
      toast.success('Cadenza avvisi aggiornata')
    } catch {
      toast.error('Errore salvataggio cadenza')
    } finally {
      setSavingCadenza(false)
    }
  }

  const fisse = Object.entries(TIPOLOGIE_DOCUMENTO_LABEL)

  if (loading) return <div className="p-8 text-gray-500">Caricamento...</div>

  return (
    <div className="p-6 max-w-4xl space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <Settings size={22} className="text-blue-600" />
        <h1 className="text-2xl font-bold text-gray-900">Impostazioni RED</h1>
      </div>

      {!loadingSolaLettura && puoGestireSolaLettura && (
        <div className={`card p-5 border-l-4 ${solaLettura ? 'border-l-amber-500' : 'border-l-gray-200'}`}>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              {solaLettura ? <Lock size={20} className="text-amber-600 mt-0.5" /> : <Unlock size={20} className="text-gray-400 mt-0.5" />}
              <div>
                <h2 className="text-lg font-semibold text-gray-800">Sola lettura modulo RED</h2>
                <p className="text-sm text-gray-500 mt-1 max-w-xl">
                  Quando attiva, tutti possono solo consultare il modulo RED — creazioni, modifiche e cancellazioni vengono bloccate
                  su ogni immobile, appalto, pagamento e documento — <b>tranne il tuo account</b>, incluso qualsiasi altro superadmin.
                  Solo tu vedi questo interruttore.
                </p>
              </div>
            </div>
            <button
              onClick={handleToggleSolaLettura}
              disabled={savingSolaLettura}
              className={solaLettura ? 'btn-secondary text-sm whitespace-nowrap' : 'btn-primary text-sm whitespace-nowrap'}
            >
              {solaLettura ? 'Disattiva sola lettura' : 'Attiva sola lettura'}
            </button>
          </div>
        </div>
      )}

      <div className="card p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Tipologie Documento</h2>
            <p className="text-sm text-gray-500 mt-1">Compaiono nel menu "Tipologia" quando si carica un documento su un immobile.</p>
          </div>
          <button onClick={() => setShowNuova(true)} className="btn-primary text-sm">+ Nuova tipologia</button>
        </div>

        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
              <th className="py-2">Nome</th>
              <th className="py-2">Codice</th>
              <th className="py-2">Stato</th>
              <th className="py-2 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {custom.map(tipo => (
              <tr key={tipo.id} className="border-b border-gray-100">
                <td className="py-2">{tipo.nome}</td>
                <td className="py-2 text-gray-400 font-mono text-xs">{tipo.codice}</td>
                <td className="py-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${tipo.attivo ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {tipo.attivo ? 'Attiva' : 'Disattivata'}
                  </span>
                </td>
                <td className="py-2 text-right">
                  <button onClick={() => handleToggleAttivo(tipo)} className="text-xs text-blue-600 hover:underline">
                    {tipo.attivo ? 'Disattiva' : 'Riattiva'}
                  </button>
                </td>
              </tr>
            ))}
            {custom.length === 0 && (
              <tr><td colSpan={4} className="py-6 text-center text-gray-400">Nessuna tipologia personalizzata creata.</td></tr>
            )}
          </tbody>
        </table>

        <details className="mt-6">
          <summary className="text-sm text-gray-500 cursor-pointer">Tipologie predefinite ({fisse.length}, sola lettura)</summary>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {fisse.map(([codice, label]) => (
              <div key={codice} className="text-xs text-gray-500 bg-gray-50 rounded px-2 py-1">{label}</div>
            ))}
          </div>
        </details>
      </div>

      <div className="card p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Operatori Avviso</h2>
            <p className="text-sm text-gray-500 mt-1">Ricevono gli avvisi email/WhatsApp per tutte le scadenze RED.</p>
          </div>
          <button onClick={() => setShowNuovoOperatore(true)} className="btn-primary text-sm">+ Nuovo operatore</button>
        </div>

        {loadingOperatori ? (
          <div className="text-gray-400 text-sm">Caricamento...</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                <th className="py-2">Nome</th>
                <th className="py-2">Email</th>
                <th className="py-2">WhatsApp</th>
                <th className="py-2">Stato</th>
                <th className="py-2 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {operatori.map(op => (
                <tr key={op.id} className="border-b border-gray-100">
                  <td className="py-2">{op.nome}</td>
                  <td className="py-2 text-gray-500">{op.email || '—'}</td>
                  <td className="py-2 text-gray-500">{op.telefono_whatsapp || '—'}</td>
                  <td className="py-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${op.attivo ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {op.attivo ? 'Attivo' : 'Disattivato'}
                    </span>
                  </td>
                  <td className="py-2 text-right space-x-3">
                    <button onClick={() => handleToggleOperatore(op)} className="text-xs text-blue-600 hover:underline">
                      {op.attivo ? 'Disattiva' : 'Riattiva'}
                    </button>
                    <button onClick={() => handleEliminaOperatore(op)} className="text-xs text-red-600 hover:underline">
                      Elimina
                    </button>
                  </td>
                </tr>
              ))}
              {operatori.length === 0 && (
                <tr><td colSpan={5} className="py-6 text-center text-gray-400">Nessun operatore configurato.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="card p-5">
        <h2 className="text-lg font-semibold text-gray-800 mb-1">Cadenza avvisi default</h2>
        <p className="text-sm text-gray-500 mb-4">Applicata alle nuove scadenze create dal sistema. Puoi comunque modificarla per singola scadenza in Scadenzario.</p>
        {loadingCadenza ? (
          <div className="text-gray-400 text-sm">Caricamento...</div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="label">Giorni di preavviso (separati da virgola)</label>
              <input className="input w-full max-w-xs" value={giorniPreavviso} onChange={e => setGiorniPreavviso(e.target.value)} placeholder="30, 7, 1" />
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={canaliEmail} onChange={e => setCanaliEmail(e.target.checked)} />
                Email
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={canaliWhatsapp} onChange={e => setCanaliWhatsapp(e.target.checked)} />
                WhatsApp
              </label>
            </div>
            <button onClick={handleSalvaCadenza} disabled={savingCadenza} className="btn-primary text-sm">
              {savingCadenza ? 'Salvataggio...' : 'Salva cadenza'}
            </button>
          </div>
        )}
      </div>

      {showNuova && (
        <NuovaTipologiaDocumentoModal
          onClose={() => setShowNuova(false)}
          onCreated={() => load()}
        />
      )}

      {showNuovoOperatore && (
        <NuovoOperatoreAvvisoModal
          onClose={() => setShowNuovoOperatore(false)}
          onCreated={() => loadOperatori()}
        />
      )}
    </div>
  )
}

export default RedImpostazioni
